import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowUpRight,
  Building2,
  Eye,
  Filter,
  LogIn,
  Pencil,
  PlusCircle,
  RefreshCw,
  Search,
  Settings2,
  Trash2,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { useAuth, PROXY_CANCELLED } from '../contexts/AuthContext';
import { getErrorMessage } from '../lib/utils';
import { workspaceAdminAPI } from '../services/api';
import { StatCard } from '../components/common/StatCard';
import { CardSkeleton, EmptyState } from '../components/common/States';

const StatusBadge = ({ status }) => {
  const normalizedStatus = String(status || 'unknown').toLowerCase();
  const variant =
    normalizedStatus === 'active'
      ? 'success'
      : normalizedStatus === 'pending'
        ? 'warning'
        : normalizedStatus === 'suspended'
          ? 'danger'
          : 'outline';

  return <Badge variant={variant}>{normalizedStatus}</Badge>;
};

const ActionButton = ({ icon: Icon, label, onClick, variant = 'outline', disabled = false, className = '' }) => (
  <Button
    type="button"
    size="icon"
    variant={variant}
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    className={`h-9 w-9 rounded-xl ${className}`}
  >
    <Icon className="h-4 w-4" />
  </Button>
);

const SuperadminOrganizationsMasterPage = () => {
  const navigate = useNavigate();
  const { proxyLogin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [proxyLoadingId, setProxyLoadingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [tenantsResponse, usersResponse] = await Promise.all([
        workspaceAdminAPI.listTenants(),
        workspaceAdminAPI.listUsers(),
      ]);

      setOrganizations(Array.isArray(tenantsResponse.data) ? tenantsResponse.data : []);
      setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load organizations'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const adminEmailByTenantId = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      if (String(user.role || '').toLowerCase() === 'workspace_admin' && !map.has(user.tenant_id)) {
        map.set(user.tenant_id, user.email);
      }
    });
    return map;
  }, [users]);

  const userCountByTenantId = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      map.set(user.tenant_id, (map.get(user.tenant_id) || 0) + 1);
    });
    return map;
  }, [users]);

  const activeUserCountByTenantId = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      if (!user.is_active) return;
      map.set(user.tenant_id, (map.get(user.tenant_id) || 0) + 1);
    });
    return map;
  }, [users]);

  const filteredOrganizations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const rows = organizations
      .map((organization) => ({
        ...organization,
        admin_email: adminEmailByTenantId.get(organization.id) || 'Not assigned',
        user_count: userCountByTenantId.get(organization.id) || 0,
        active_user_count: activeUserCountByTenantId.get(organization.id) || 0,
        initials: String(organization.name || 'O')
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0]?.toUpperCase() || '')
          .join(''),
      }))
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());

    const statusScopedRows =
      statusFilter === 'all'
        ? rows
        : rows.filter((organization) => String(organization.status || '').toLowerCase() === statusFilter);

    if (!query) return statusScopedRows;

    return statusScopedRows.filter((organization) =>
      [
        organization.name,
        organization.slug,
        organization.status,
        organization.subscription_plan_name,
        organization.admin_email,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [activeUserCountByTenantId, adminEmailByTenantId, organizations, searchQuery, statusFilter, userCountByTenantId]);

  const stats = useMemo(() => {
    const active = organizations.filter((row) => String(row.status).toLowerCase() === 'active').length;
    const pending = organizations.filter((row) => String(row.status).toLowerCase() === 'pending').length;
    const suspended = organizations.filter((row) => String(row.status).toLowerCase() === 'suspended').length;

    return {
      total: organizations.length,
      active,
      pending,
      suspended,
      users: users.length,
    };
  }, [organizations, users.length]);

  const formatRelativeAge = (value) => {
    if (!value) return 'Recently created';
    const createdAt = new Date(value);
    if (Number.isNaN(createdAt.getTime())) return 'Recently created';

    const diffMs = Date.now() - createdAt.getTime();
    const diffDays = Math.max(Math.floor(diffMs / (24 * 60 * 60 * 1000)), 0);
    if (diffDays === 0) return 'Created today';
    if (diffDays === 1) return 'Created 1 day ago';
    if (diffDays < 30) return `Created ${diffDays} days ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return `Created ${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  };

  const openViewDialog = (organization) => {
    setSelectedOrganization({
      ...organization,
      admin_email: adminEmailByTenantId.get(organization.id) || 'Not assigned',
      user_count: userCountByTenantId.get(organization.id) || 0,
      active_user_count: activeUserCountByTenantId.get(organization.id) || 0,
    });
    setShowViewDialog(true);
  };

  const openDeleteDialog = (organization) => {
    setSelectedOrganization(organization);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedOrganization?.id) return;

    try {
      setSubmitting(true);
      await workspaceAdminAPI.deleteTenant(selectedOrganization.id);
      toast.success('Organization deleted');
      setShowDeleteDialog(false);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete organization'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleManageOrganization = (organization) => {
    navigate(`/superadmin/tenants/edit/${organization.id}`);
  };

  const handleCreateOrganization = () => {
    navigate('/superadmin/tenants/create');
  };

  const handleOpenTenantsModule = () => {
    navigate('/superadmin/tenants/list');
  };

  const handleProxyTenantLogin = async (organization) => {
    try {
      setProxyLoadingId(organization.id);
      const session = await proxyLogin(organization.slug);
      const role = String(session?.role || '').toLowerCase();
      if (role === 'superadmin') {
        toast.success(`Already in ${organization.name}`);
        navigate('/superadmin');
        return;
      }
      toast.success(`Proxy login successful: ${organization.name}`);
      navigate('/admin');
    } catch (error) {
      if (error?.code === PROXY_CANCELLED) return;
      toast.error(getErrorMessage(error, 'Proxy login failed'));
    } finally {
      setProxyLoadingId(null);
    }
  };

  const filterChips = [
    { key: 'all', label: 'All Companies', value: stats.total },
    { key: 'active', label: 'Active', value: stats.active },
    { key: 'pending', label: 'Pending', value: stats.pending },
    { key: 'suspended', label: 'Suspended', value: stats.suspended },
  ];

  return (
    <div className="space-y-6">
      <Card className="group relative overflow-hidden border-border bg-[radial-gradient(circle_at_top_left,rgba(8,110,244,0.10),transparent_26%),linear-gradient(135deg,hsl(var(--card)),hsl(var(--card))_46%,rgba(8,110,244,0.04))] shadow-sm">
        <CardContent className="pt-6">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-16 top-0 h-40 w-40 rounded-full bg-primary/10 blur-3xl transition duration-700 group-hover:scale-110" />
            <div className="absolute right-0 top-8 h-44 w-44 rounded-full bg-primary/10 blur-3xl transition duration-700 group-hover:scale-110" />
          </div>
          <div className="relative grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary shadow-sm">
                <Building2 className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-12" />
                Master registry
              </div>
              <div>
                <h2 className="text-3xl font-semibold tracking-tight text-foreground">Organizations</h2>
                <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
                  Companies-style organization management with quick actions, richer company details, and faster tenant operations.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-4">
                <StatCard label="Organizations" value={stats.total} icon={Building2} tone="primary" />
                <StatCard label="Active" value={stats.active} tone="success" />
                <StatCard label="Pending" value={stats.pending} tone="warning" />
                <StatCard label="Suspended" value={stats.suspended} tone="danger" />
              </div>
            </div>

            <div className="space-y-3 xl:flex xl:flex-col xl:items-end xl:justify-between">
              <StatCard
                className="w-full"
                label="Workspace Users"
                value={stats.users}
                icon={Users}
                tone="info"
                hint="Mapped across all registered companies"
              />
              <div className="grid w-full gap-3 sm:grid-cols-2 xl:grid-cols-1">
                <Button onClick={handleCreateOrganization}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Add Company
                </Button>
                <Button variant="outline" onClick={handleOpenTenantsModule}>
                  <ArrowUpRight className="mr-2 h-4 w-4" />
                  Open Tenants
                </Button>
                <Button variant="outline" onClick={loadData}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border shadow-sm">
        <CardHeader className="space-y-4 border-b border-border/50">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Company Directory</CardTitle>
              <CardDescription>Reference-inspired companies listing with stronger actions and faster browsing.</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                <Filter className="h-3.5 w-3.5" />
                {filteredOrganizations.length} visible
              </div>
              <Button size="sm" variant="outline" className="rounded-xl" onClick={loadData}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Sync
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {filterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setStatusFilter(chip.key)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                    statusFilter === chip.key
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-primary'
                  }`}
                >
                  {chip.label} ({chip.value})
                </button>
              ))}
            </div>
            <div className="relative w-full lg:w-80">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search company, slug, plan, admin..."
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid gap-4 border-b border-border/50 bg-muted/40 p-4 md:grid-cols-3">
            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Quick Create</p>
              <p className="mt-2 text-sm font-semibold text-foreground">Register a new company workspace</p>
              <p className="mt-1 text-xs text-muted-foreground">Create a company, define its slug, and assign the default admin credentials.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Quick Manage</p>
              <p className="mt-2 text-sm font-semibold text-foreground">Open tenant configuration instantly</p>
              <p className="mt-1 text-xs text-muted-foreground">Jump to full tenant editing for plan assignment, limits, and workspace setup.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Proxy Access</p>
              <p className="mt-2 text-sm font-semibold text-foreground">Validate the tenant experience directly</p>
              <p className="mt-1 text-xs text-muted-foreground">Login to a company session from the action bar without leaving the superadmin flow.</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1220px]">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Company</th>
                  <th className="p-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Admin</th>
                  <th className="p-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Plan</th>
                  <th className="p-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Users</th>
                  <th className="p-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Status</th>
                  <th className="p-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Created</th>
                  <th className="p-4 text-right text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-4">
                      <CardSkeleton rows={6} className="border-0 shadow-none" />
                    </td>
                  </tr>
                ) : filteredOrganizations.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState
                        icon={Building2}
                        title="No organizations found"
                        description="Try adjusting your search or status filter, or register a new company workspace."
                        actionLabel="Add Company"
                        onAction={handleCreateOrganization}
                      />
                    </td>
                  </tr>
                ) : (
                  filteredOrganizations.map((organization) => (
                    <tr key={organization.id} className="border-b last:border-0 transition-colors hover:bg-muted/50">
                      <td className="p-4 align-top">
                        <div className="flex items-start gap-3">
                          <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-sm font-semibold text-primary-foreground shadow-sm">
                            {organization.initials || 'O'}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">{organization.name}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{organization.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 align-top">
                        <p className="text-sm font-medium text-foreground">{organization.admin_email}</p>
                        <p className="mt-1 text-xs text-muted-foreground">Workspace administrator</p>
                      </td>
                      <td className="p-4 align-top">
                        <Badge variant={organization.subscription_plan_name ? 'soft' : 'outline'}>
                          {organization.subscription_plan_name || 'Unassigned'}
                        </Badge>
                      </td>
                      <td className="p-4 align-top">
                        <p className="text-sm font-semibold text-foreground">{organization.user_count}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{organization.active_user_count} active</p>
                      </td>
                      <td className="p-4 align-top">
                        <StatusBadge status={organization.status} />
                      </td>
                      <td className="p-4 align-top">
                        <p className="text-sm text-foreground">
                          {organization.created_at ? new Date(organization.created_at).toLocaleDateString() : 'N/A'}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">{formatRelativeAge(organization.created_at)}</p>
                      </td>
                      <td className="p-4 align-top">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <ActionButton icon={Eye} label="View" onClick={() => openViewDialog(organization)} />
                          <ActionButton icon={Pencil} label="Edit" onClick={() => handleManageOrganization(organization)} />
                          <ActionButton icon={Settings2} label="Manage" onClick={() => handleManageOrganization(organization)} />
                          <ActionButton
                            icon={LogIn}
                            label={proxyLoadingId === organization.id ? 'Opening...' : 'Login'}
                            onClick={() => handleProxyTenantLogin(organization)}
                            disabled={proxyLoadingId === organization.id}
                          />
                          <ActionButton
                            icon={Trash2}
                            label="Delete"
                            onClick={() => openDeleteDialog(organization)}
                            className="border-danger/30 text-danger hover:bg-danger/10 hover:text-danger"
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Organization Details</DialogTitle>
          </DialogHeader>
          {selectedOrganization ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-muted/50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Organization</p>
                <p className="mt-2 text-sm font-semibold text-foreground">{selectedOrganization.name}</p>
              </div>
              <div className="rounded-xl border border-border bg-muted/50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Slug</p>
                <p className="mt-2 text-sm font-semibold text-foreground">{selectedOrganization.slug}</p>
              </div>
              <div className="rounded-xl border border-border bg-muted/50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Admin Email</p>
                <p className="mt-2 text-sm font-semibold text-foreground">{selectedOrganization.admin_email}</p>
              </div>
              <div className="rounded-xl border border-border bg-muted/50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Plan</p>
                <p className="mt-2 text-sm font-semibold text-foreground">{selectedOrganization.subscription_plan_name || 'Unassigned'}</p>
              </div>
              <div className="rounded-xl border border-border bg-muted/50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Status</p>
                <div className="mt-2">
                  <StatusBadge status={selectedOrganization.status} />
                </div>
              </div>
              <div className="rounded-xl border border-border bg-muted/50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Created</p>
                <p className="mt-2 text-sm font-semibold text-foreground">
                  {selectedOrganization.created_at ? new Date(selectedOrganization.created_at).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div className="rounded-xl border border-border bg-muted/50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Users</p>
                <p className="mt-2 text-sm font-semibold text-foreground">{selectedOrganization.user_count || 0}</p>
              </div>
              <div className="rounded-xl border border-border bg-muted/50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Active Users</p>
                <p className="mt-2 text-sm font-semibold text-foreground">{selectedOrganization.active_user_count || 0}</p>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Delete <span className="font-semibold text-foreground">{selectedOrganization?.name}</span>? This action cannot be undone and will fail if the organization still has linked users or employees.
            </p>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button type="button" variant="destructive" onClick={handleDelete} disabled={submitting}>
                {submitting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperadminOrganizationsMasterPage;
